/**
 * PokemonDetail Page - Complete Pokemon information
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronLeft, ChevronRight, Heart, ArrowLeft, 
  Ruler, Weight, Sparkles, Star
} from 'lucide-react';
import { usePokemon } from '../hooks/usePokemon';
import { useApp } from '../context/AppContext';
import { TypeBadgeGroup } from '../components/pokedex/TypeBadge';
import { StatsPanel } from '../components/pokedex/StatsBar';
import { AbilityList } from '../components/pokedex/AbilityCard';
import { MovesList } from '../components/pokedex/MovesList';
import { EvolutionChain } from '../components/pokedex/EvolutionChain';
import { TypeCalculator } from '../components/pokedex/TypeCalculator';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { trackEvent } from '../lib/analytics';

export default function PokemonDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, language, isFavorite, toggleFavorite } = useApp();
  const { 
    getPokemon, getPokemonSpecies, getSprites, 
    getGeneration, getLocalizedName, getLocalizedFlavorText 
  } = usePokemon();
  
  const [pokemon, setPokemon] = useState(null);
  const [species, setSpecies] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showShiny, setShowShiny] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  const pokemonId = parseInt(id);
  const favorite = isFavorite(pokemonId);
  
  // Fetch pokemon data
  useEffect(() => {
    let mounted = true;
    
    const fetchData = async () => {
      setLoading(true);
      setImageLoaded(false);
      
      const [pokemonData, speciesData] = await Promise.all([
        getPokemon(id),
        getPokemonSpecies(id)
      ]);
      
      if (mounted) {
        setPokemon(pokemonData);
        setSpecies(speciesData);
        setLoading(false);
        trackEvent('pokemon_view', {
          pokemonName: pokemonData?.name || id,
          pokemonId: parseInt(id),
        });
      }
    };
    
    fetchData();
    return () => { mounted = false; };
  }, [id, getPokemon, getPokemonSpecies]);
  
  const sprites = pokemon ? getSprites(pokemon) : null;
  const types = pokemon?.types?.map(t => t.type.name) || [];
  const generation = getGeneration(pokemonId);
  
  // Get localized data
  const localizedName = species ? getLocalizedName(species.names) : pokemon?.name;
  const description = species ? getLocalizedFlavorText(species.flavor_text_entries) : '';
  const genus = species?.genera?.find(g => g.language.name === (language === 'es' ? 'es' : 'en'))?.genus || '';
  
  // Navigation
  const hasPrevious = pokemonId > 1;
  const hasNext = pokemonId < 1025;
  
  const goToPrevious = () => {
    if (hasPrevious) navigate(`/pokemon/${pokemonId - 1}`);
  };
  
  const goToNext = () => {
    if (hasNext) navigate(`/pokemon/${pokemonId + 1}`);
  };
  
  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [pokemonId]);
  
  if (loading) {
    return <PokemonDetailSkeleton />;
  }
  
  if (!pokemon) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-muted-foreground mb-4">{t('error')}</p>
          <Button onClick={() => navigate('/')}>{t('home')}</Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen" data-testid="pokemon-detail-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Back button and navigation */}
        <div className="flex items-center justify-between mb-6">
          <Link to="/">
            <Button variant="ghost" size="sm" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('home')}
            </Button>
          </Link>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={goToPrevious}
              disabled={!hasPrevious}
              data-testid="prev-pokemon-btn"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <span className="text-sm text-muted-foreground min-w-[60px] text-center">
              #{String(pokemonId).padStart(3, '0')}
            </span>
            <Button
              variant="outline"
              size="icon"
              onClick={goToNext}
              disabled={!hasNext}
              data-testid="next-pokemon-btn"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
        
        {/* Main content */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Left column - Image and basic info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-5"
          >
            <div className="bg-card rounded-3xl border border-border shadow-lg overflow-hidden">
              {/* Image section */}
              <div className="relative bg-gradient-to-br from-violet-100 to-rose-100 dark:from-violet-900/30 dark:to-rose-900/30 p-8">
                {/* ID watermark */}
                <span className="absolute top-4 left-4 text-7xl font-black text-black/5 dark:text-white/5 select-none">
                  #{String(pokemonId).padStart(3, '0')}
                </span>
                
                {/* Favorite button */}
                <button
                  onClick={() => toggleFavorite(pokemonId)}
                  data-testid="detail-favorite-btn"
                  className="absolute top-4 right-4 p-3 rounded-full bg-white/80 dark:bg-black/50 backdrop-blur-sm hover:scale-110 transition-transform z-10"
                >
                  <Heart 
                    className={`w-6 h-6 transition-colors ${
                      favorite 
                        ? 'fill-rose-500 text-rose-500' 
                        : 'text-muted-foreground hover:text-rose-500'
                    }`}
                  />
                </button>
                
                {/* Shiny toggle */}
                <button
                  onClick={() => setShowShiny(!showShiny)}
                  data-testid="shiny-toggle"
                  className={`absolute bottom-4 right-4 p-2 rounded-full backdrop-blur-sm transition-all z-10 ${
                    showShiny 
                      ? 'bg-yellow-400/80 text-yellow-900' 
                      : 'bg-white/80 dark:bg-black/50 text-muted-foreground'
                  }`}
                >
                  <Star className={`w-5 h-5 ${showShiny ? 'fill-current' : ''}`} />
                </button>
                
                {/* Pokemon image */}
                <div className="relative flex items-center justify-center h-64 sm:h-80">
                  {!imageLoaded && (
                    <Skeleton className="absolute w-48 h-48 rounded-full" />
                  )}
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={showShiny ? 'shiny' : 'normal'}
                      src={showShiny ? sprites?.shiny : sprites?.default}
                      alt={pokemon.name}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: imageLoaded ? 1 : 0, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{ duration: 0.3 }}
                      onLoad={() => setImageLoaded(true)}
                      className="w-48 h-48 sm:w-64 sm:h-64 object-contain drop-shadow-2xl"
                    />
                  </AnimatePresence>
                </div>
              </div>
              
              {/* Basic info */}
              <div className="p-6 space-y-4">
                <div>
                  <p className="text-sm font-bold text-violet-600 dark:text-violet-400 uppercase tracking-widest mb-1">
                    #{String(pokemonId).padStart(3, '0')} • {t('generation')} {generation}
                  </p>
                  <h1 className="text-3xl sm:text-4xl font-bold capitalize text-foreground">
                    {localizedName?.replace(/-/g, ' ')}
                  </h1>
                  {genus && (
                    <p className="text-muted-foreground mt-1">{genus}</p>
                  )}
                </div>
                
                <TypeBadgeGroup types={types} size="md" />
                
                {/* Physical stats */}
                <div className="grid grid-cols-2 gap-4 pt-2">
                  <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-xl">
                    <Ruler className="w-5 h-5 text-violet-500" />
                    <div>
                      <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('height')}</p>
                      <p className="font-semibold text-foreground">{(pokemon.height / 10).toFixed(1)} m</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-xl">
                    <Weight className="w-5 h-5 text-rose-500" />
                    <div>
                      <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('weight')}</p>
                      <p className="font-semibold text-foreground">{(pokemon.weight / 10).toFixed(1)} kg</p>
                    </div>
                  </div>
                </div>
                
                {/* Description */}
                {description && (
                  <div className="pt-2">
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      {description}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
          
          {/* Right column - Tabs with detailed info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-7"
          >
            <div className="bg-card rounded-3xl border border-border shadow-lg p-4 sm:p-6">
              <Tabs defaultValue="stats" className="w-full">
                <TabsList className="flex flex-wrap w-full mb-6 h-auto gap-1">
                  <TabsTrigger value="stats" data-testid="tab-stats" className="flex-1 min-w-[70px] py-2 text-xs sm:text-sm">{t('stats')}</TabsTrigger>
                  <TabsTrigger value="abilities" data-testid="tab-abilities" className="flex-1 min-w-[70px] py-2 text-xs sm:text-sm">{t('abilities')}</TabsTrigger>
                  <TabsTrigger value="moves" data-testid="tab-moves" className="flex-1 min-w-[70px] py-2 text-xs sm:text-sm">{t('moves')}</TabsTrigger>
                  <TabsTrigger value="evolution" data-testid="tab-evolution" className="flex-1 min-w-[70px] py-2 text-xs sm:text-sm">{t('evolution')}</TabsTrigger>
                </TabsList>
                
                <TabsContent value="stats" className="space-y-6">
                  <StatsPanel stats={pokemon.stats} />
                  <div className="pt-4 border-t border-border">
                    <h3 className="text-lg font-bold text-foreground mb-4">
                      {t('weaknesses')} & {t('resistances')}
                    </h3>
                    <TypeCalculator types={types} />
                  </div>
                </TabsContent>
                
                <TabsContent value="abilities">
                  <AbilityList abilities={pokemon.abilities} />
                </TabsContent>
                
                <TabsContent value="moves">
                  <MovesList moves={pokemon.moves} />
                </TabsContent>
                
                <TabsContent value="evolution">
                  <EvolutionChain speciesData={species} />
                </TabsContent>
              </Tabs>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

function PokemonDetailSkeleton() {
  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between mb-6">
          <Skeleton className="h-9 w-24" />
          <div className="flex items-center gap-2">
            <Skeleton className="h-9 w-9" />
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-9 w-9" />
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          <div className="lg:col-span-5">
            <div className="bg-card rounded-3xl border border-border overflow-hidden">
              <Skeleton className="h-80 w-full" />
              <div className="p-6 space-y-4">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-10 w-48" />
                <div className="flex gap-2">
                  <Skeleton className="h-7 w-20 rounded-full" />
                  <Skeleton className="h-7 w-20 rounded-full" />
                </div>
              </div>
            </div>
          </div>
          
          <div className="lg:col-span-7">
            <div className="bg-card rounded-3xl border border-border p-6">
              <Skeleton className="h-10 w-full mb-6" />
              <div className="space-y-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-8 w-full" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
